import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { IconBack, IconClose, IconViewer } from './icons';
import { LiveSessionPublic, MINI_COLLAPSED_KEY, MINI_H, MINI_POS_KEY, MINI_W } from './utils';
import { YoutubePlayer, YoutubePlayerHandle, sessionYoutubeId } from './players';

export type MiniPlayerHandle = {
  /** Call inside close-from-full gesture so unmuted autoplay is allowed on iOS. */
  startPlayback: () => void;
};

interface Props {
  session: LiveSessionPublic;
  muted: boolean;
  onMutedChange: (muted: boolean) => void;
  viewerCount: number;
  onOpen: () => void;
}

const EDGE = 8;
const V_EDGE = 12;

function formatViewers(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(Math.max(0, Math.floor(n)));
}

function bounds() {
  return {
    minX: EDGE,
    maxX: Math.max(EDGE, window.innerWidth - MINI_W - EDGE),
    minY: EDGE,
    maxY: Math.max(EDGE, window.innerHeight - MINI_H - EDGE),
  };
}

function clamp(x: number, y: number) {
  const b = bounds();
  return {
    x: Math.min(b.maxX, Math.max(b.minX, x)),
    y: Math.min(b.maxY, Math.max(b.minY, y)),
  };
}

/** Snap to nearest screen edge (magnetic PiP). */
function magnetize(x: number, y: number) {
  const b = bounds();
  const midX = (b.minX + b.maxX) / 2;
  const snappedX = x < midX ? b.minX : b.maxX;

  // Soft vertical snap when near top/bottom
  const nearTop = y - b.minY < V_EDGE * 2;
  const nearBottom = b.maxY - y < V_EDGE * 2;
  let snappedY = Math.min(b.maxY, Math.max(b.minY, y));
  if (nearTop) snappedY = b.minY;
  else if (nearBottom) snappedY = b.maxY;

  return { x: snappedX, y: snappedY };
}

function defaultPos() {
  const b = bounds();
  return { x: b.maxX, y: Math.max(b.minY, b.maxY - 16) };
}

function loadPos() {
  try {
    const raw = localStorage.getItem(MINI_POS_KEY);
    if (!raw) return defaultPos();
    const parsed = JSON.parse(raw);
    if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
      return magnetize(parsed.x, parsed.y);
    }
  } catch {
    // ignore
  }
  return defaultPos();
}

function loadCollapsed(): boolean {
  try {
    return localStorage.getItem(MINI_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function persistCollapsed(value: boolean) {
  try {
    localStorage.setItem(MINI_COLLAPSED_KEY, value ? '1' : '0');
  } catch {
    // ignore
  }
}

function persistPos(next: { x: number; y: number }) {
  try {
    localStorage.setItem(MINI_POS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

function dockSide(x: number): 'left' | 'right' {
  const b = bounds();
  return x < (b.minX + b.maxX) / 2 ? 'left' : 'right';
}

export const MiniPlayer = forwardRef<MiniPlayerHandle, Props>(function MiniPlayer(
  { session, muted, onMutedChange, viewerCount, onOpen },
  ref
) {
  const [pos, setPos] = useState(loadPos);
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  const [dragging, setDragging] = useState(false);
  const [ytKey, setYtKey] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const ytRef = useRef<YoutubePlayerHandle>(null);
  const playerMuted = muted || collapsed;
  const side = dockSide(pos.x);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    moved: boolean;
  } | null>(null);
  const youtubeId = sessionYoutubeId(session);

  useImperativeHandle(ref, () => ({
    startPlayback: () => {
      // Closing fullscreen always restores the PiP (not the docked tab).
      setCollapsed(false);
      persistCollapsed(false);
      setYtKey((k) => k + 1);
      queueMicrotask(() => {
        if (muted) ytRef.current?.mute();
        else ytRef.current?.unmute();
        ytRef.current?.play();
      });
    },
  }));

  useEffect(() => {
    const onResize = () => setPos((p) => magnetize(p.x, p.y));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Keep YT mute in sync if preference changes while mini is mounted.
  // Docked/collapsed is always muted so audio never plays from the pulltab.
  useEffect(() => {
    if (playerMuted) ytRef.current?.mute();
    else ytRef.current?.unmute();
  }, [playerMuted]);

  // React registers root touch listeners as passive — use native non-passive
  // listeners so preventDefault can stop the storefront from scrolling.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const blockScroll = (e: TouchEvent) => {
      // preventDefault on chrome kills iOS click/tap; close/tab handle their own gestures.
      if (e.target instanceof Element && e.target.closest('[data-il-chrome]')) return;
      e.preventDefault();
    };

    el.addEventListener('touchstart', blockScroll, { passive: false });
    el.addEventListener('touchmove', blockScroll, { passive: false });
    return () => {
      el.removeEventListener('touchstart', blockScroll);
      el.removeEventListener('touchmove', blockScroll);
    };
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [dragging]);

  const expand = () => {
    setCollapsed(false);
    persistCollapsed(false);
    queueMicrotask(() => {
      ytRef.current?.mute();
      ytRef.current?.play();
    });
  };

  const collapse = () => {
    setPos((p) => {
      const next = magnetize(p.x, p.y);
      persistPos(next);
      return next;
    });
    setCollapsed(true);
    persistCollapsed(true);
    onMutedChange(true);
  };

  const expandRef = useRef(expand);
  const collapseRef = useRef(collapse);
  expandRef.current = expand;
  collapseRef.current = collapse;

  // iOS: parent touch-action/preventDefault can drop React click; touchend still fires.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onTouchEnd = (e: TouchEvent) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest('[data-il-chrome="close"]')) {
        e.preventDefault();
        e.stopPropagation();
        collapseRef.current();
        return;
      }
      if (t.closest('[data-il-chrome="tab"]')) {
        e.preventDefault();
        e.stopPropagation();
        expandRef.current();
      }
    };
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    return () => el.removeEventListener('touchend', onTouchEnd);
  }, []);

  const chromeIntent = (target: EventTarget | null): 'open' | 'close' | 'tab' => {
    if (!(target instanceof Element)) return 'open';
    if (target.closest('[data-il-chrome="close"]')) return 'close';
    if (target.closest('[data-il-chrome="tab"]')) return 'tab';
    return 'open';
  };

  const onPointerDown = (e: React.PointerEvent) => {
    const intent = chromeIntent(e.target);
    // Close/tab use their own pointerup + native touchend (iOS drops click after preventDefault).
    if (intent === 'close' || intent === 'tab') return;
    if (collapsed) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
      moved: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
      d.moved = true;
      setDragging(true);
    }
    if (d.moved) {
      setPos(clamp(d.origX + dx, d.origY + dy));
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = null;
    setDragging(false);
    if (!d.moved) {
      onOpen();
      return;
    }
    setPos((p) => {
      const next = magnetize(p.x, p.y);
      persistPos(next);
      return next;
    });
  };

  const onClosePointerUp = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = null;
    collapse();
  };

  const onTabPointerUp = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = null;
    expand();
  };

  return (
    <div
      ref={rootRef}
      className={[
        'il-mini',
        dragging ? 'is-dragging' : '',
        collapsed ? 'is-collapsed' : '',
        collapsed ? `is-collapsed-${side}` : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        left: collapsed
          ? side === 'right'
            ? pos.x + MINI_W + EDGE
            : pos.x - MINI_W - EDGE
          : pos.x,
        top: pos.y,
        width: MINI_W,
        height: MINI_H,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      aria-label={collapsed ? undefined : `Live mini player, ${viewerCount} viewers`}
    >
      <div className="il-mini-video">
        {youtubeId ? (
          <YoutubePlayer
            key={`yt-mini-${ytKey}-${youtubeId}`}
            ref={ytRef}
            urlOrId={youtubeId}
            active
            muted={playerMuted}
            autoplay
            controls={false}
            className="il-mini-yt"
          />
        ) : (
          <div className="il-mini-empty">LIVE</div>
        )}
      </div>
      <div className="il-mini-hit" aria-hidden />
      <div className="il-mini-chrome">
        <div className="il-mini-meta" aria-hidden>
          <span className="il-live-tag">
            <span className="il-live-tag-dot" />
            LIVE
          </span>
          <span className="il-viewer-chip">
            <IconViewer size={10} />
            {formatViewers(viewerCount)}
          </span>
        </div>
        <button
          type="button"
          className="il-mini-close"
          data-il-chrome="close"
          aria-label="Hide live player"
          tabIndex={collapsed ? -1 : 0}
          onPointerUp={onClosePointerUp}
        >
          <IconClose size={14} />
        </button>
      </div>
      <button
        type="button"
        className="il-mini-tab"
        data-il-chrome="tab"
        aria-label="Show live player"
        tabIndex={collapsed ? 0 : -1}
        onPointerUp={onTabPointerUp}
      >
        <span className="il-mini-tab-dot" />
        <IconBack size={14} />
      </button>
    </div>
  );
});
